import { OrganizationModule } from '@shared/api/graphql/generated';

export const COMPANY_SETTINGS_PERMISSIONS = ['settings.view', 'settings.update'];
export const LOCATION_SETTINGS_PERMISSIONS = [
  'locations.view',
  'locations.manage',
  'booking_settings.view',
  'booking_settings.manage',
  'fiscalization.manage',
];
export const CLIENT_SEGMENTS_PERMISSION = 'client_segments.manage';
export const ADD_ONS_PERMISSION = 'addons.manage';

interface SettingsAccessPredicates {
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  hasModule: (module: OrganizationModule) => boolean;
}

export interface SettingsAccess {
  company: boolean;
  locations: boolean;
  team: boolean;
  clients: boolean;
  loyalty: boolean;
  services: boolean;
  tireService: boolean;
  addOns: boolean;
  any: boolean;
}

const canManageClientSegments = (access: SettingsAccessPredicates): boolean =>
  access.hasPermission(CLIENT_SEGMENTS_PERMISSION) && access.hasModule(OrganizationModule.Clients);

const canManageTireService = (access: SettingsAccessPredicates): boolean =>
  access.hasPermission('tire_service.settings') && access.hasModule(OrganizationModule.TireService);

export const getSettingsAccess = (access: SettingsAccessPredicates): SettingsAccess => {
  const company = access.hasAnyPermission(COMPANY_SETTINGS_PERMISSIONS);
  const locations = access.hasAnyPermission(LOCATION_SETTINGS_PERMISSIONS);
  const team = access.hasPermission('employees.manage');
  const clients = canManageClientSegments(access);
  const loyalty = access.hasPermission('loyalty.manage') && access.hasModule(OrganizationModule.Loyalty);
  const services = access.hasPermission('catalog.manage');
  const tireService = canManageTireService(access);
  const addOns = access.hasPermission(ADD_ONS_PERMISSION);

  return {
    company,
    locations,
    team,
    clients,
    loyalty,
    services,
    tireService,
    addOns,
    any: company || locations || team || clients || loyalty || services || tireService || addOns,
  };
};

/** Вход в каталог настроек требует прав управления; прав просмотра администратора недостаточно. */
export const canAccessSettings = (access: SettingsAccessPredicates): boolean =>
  access.hasAnyPermission([
    'settings.update',
    'locations.manage',
    'booking_settings.manage',
    'employees.manage',
    'fiscalization.manage',
    'catalog.manage',
    ADD_ONS_PERMISSION,
  ]) ||
  (access.hasPermission('loyalty.manage') && access.hasModule(OrganizationModule.Loyalty)) ||
  canManageClientSegments(access) ||
  canManageTireService(access);

/** Запросы на возврат живут в модуле заказов: без модуля сервер их не отдаёт даже при праве решать. */
export const canApproveRefunds = (access: Pick<SettingsAccessPredicates, 'hasPermission' | 'hasModule'>): boolean =>
  access.hasPermission('payments.refund.approve') && access.hasModule(OrganizationModule.Orders);
